import { createRouter, createWebHistory } from 'vue-router';
import posthog from 'posthog-js';
import store from './store';
import instance from '@/api/instance.js';
import popups from '@/plugins/popups/popups';
import tolgee from '@/tolgee.js';
import { recordNotificationArrival } from '@/lib/notification-attribution';

let routes = [
  {
    path: '',
    name: 'route-main',
    prefix: 'multiple',
    page: 'landing',
    meta: { hasNavbar: false },
    beforeEnter: async (to, from, next) => {
      const err = to.query.error;
      if (err) popups.popupError(tolgee.t(`error.${err}.text`), tolgee.t(`error.${err}.title`));
      if (localStorage.getItem('accessToken')) {
        return next({
          name: 'route-student',
          params: { locale: to.params.locale }
        });
      }
      return next();
    }
  },
  {
    path: 'callback/login',
    name: 'route-callback-login',
    beforeEnter: async (to, from, next) => {
      localStorage.setItem('accessToken', true);
      await store.dispatch('getUserdata');
      const { subjectId, groupId, originalUrl, error } = to.query;
      if (error) popups.popupError(tolgee.t(`error.${error}.text`), tolgee.t(`error.${error}.title`));
      if (groupId) {
        await instance.post('/join-group', {
          email: store.state.userdata?.email,
          subjectId,
          groupId
        });
      }
      // Returned, not just called: without this the fall-through below calls
      // next() a second time and the course destination is discarded, which
      // is exactly where an enrolment link is supposed to land.
      if (originalUrl && originalUrl != '/auth') return next(originalUrl);
      return next({
        name: 'route-main',
        params: { locale: to.params.locale }
      });
    }
  },
  {
    path: 'student',
    prefix: 'multiple',
    page: 'page-main',
    meta: { requiresAuth: true },
    props: {
      tabs: [
        { icon: 'courses', label: 'router.tab.courses', route: 'route-student', isSubjectAgnostic: true },
        { icon: 'calendar', label: 'router.tab.calendar', route: 'route-student-calendar' }
      ]
    },
    children: [
      {
        path: 'courses',
        name: 'route-student',
        prefix: 'student',
        page: 'page-student-courses',
        dispatch: 'getCourses'
      },
      {
        path: 'calendar/:subjectId',
        name: 'route-student-calendar',
        prefix: 'multiple',
        page: 'page-calendar'
      }
    ]
  },
  {
    path: 'teacher',
    prefix: 'multiple',
    page: 'page-main',
    meta: { requiresAuth: true },
    props: {
      tabs: [
        { icon: 'courses', label: 'router.tab.courses', route: 'route-teacher', isSubjectAgnostic: true },
        { icon: 'groups', label: 'router.tab.my_groups', route: 'route-teacher-groups' },
        { icon: 'calendar', label: 'router.tab.calendar', route: 'route-teacher-calendar' }
      ]
    },
    children: [
      {
        path: 'courses',
        name: 'route-teacher',
        prefix: 'teacher',
        page: 'page-teacher-courses',
        dispatch: 'getCourses'
      },
      {
        path: 'groups/:subjectId',
        name: 'route-teacher-groups',
        prefix: 'multiple',
        page: 'page-groups'
      },
      {
        path: 'calendar/:subjectId',
        name: 'route-teacher-calendar',
        prefix: 'multiple',
        page: 'page-calendar'
      }
    ]
  },
  {
    path: 'admin',
    prefix: 'multiple',
    page: 'page-main',
    meta: { requiresAuth: true },
    props: {
      tabs: [
        { icon: 'courses', label: 'router.tab.courses', route: 'route-admin', isSubjectAgnostic: true },
        { icon: 'groups', label: 'router.tab.groups', route: 'route-admin-groups' },
        { icon: 'calendar', label: 'router.tab.calendar', route: 'route-admin-calendar' },
        { icon: 'summary', label: 'router.tab.report', route: 'route-admin-summary' },
        { icon: 'users', label: 'router.tab.users', route: 'route-admin-students' },
        { icon: 'media', label: 'router.tab.media_library', route: 'route-admin-media' }
      ]
    },
    children: [
      {
        path: 'courses',
        name: 'route-admin',
        prefix: 'admin',
        page: 'page-admin-courses'
      },
      {
        path: 'students/:subjectId',
        name: 'route-admin-students',
        prefix: 'admin',
        page: 'page-admin-students'
      },
      {
        path: 'groups/:subjectId',
        name: 'route-admin-groups',
        prefix: 'multiple',
        page: 'page-groups'
      },
      {
        path: 'calendar/:subjectId',
        name: 'route-admin-calendar',
        prefix: 'multiple',
        page: 'page-calendar'
      },
      {
        path: 'roles/:subjectId',
        name: 'route-admin-roles',
        prefix: 'admin',
        page: 'page-admin-roles'
      },
      {
        path: 'summary/:subjectId',
        name: 'route-admin-summary',
        prefix: 'admin',
        page: 'page-admin-summary',
        dispatch: 'getStatsSubject'
      },
      {
        path: 'media/:subjectId',
        name: 'route-admin-media',
        prefix: 'multiple',
        page: 'page-media',
        dispatch: 'getStatsSubject'
      }
    ]
  },
  {
    path: 'superadmin',
    prefix: 'multiple',
    page: 'page-main',
    meta: { requiresAuth: true },
    props: {
      tabs: [
        { icon: 'courses', label: 'router.tab.courses', route: 'route-superadmin', isSubjectAgnostic: true },
        { icon: 'groups', label: 'router.tab.groups', route: 'route-superadmin-groups' },
        { icon: 'users', label: 'router.tab.users', route: 'route-superadmin-students' },
        //{ icon: "calendar", label: 'router.tab.calendar', route: 'route-superadmin-calendar' },
        { icon: 'settings', label: 'router.tab.settings', route: 'route-superadmin-subject-settings' },
        { icon: 'media', label: 'router.tab.media_library', route: 'route-superadmin-media', isSubjectAgnostic: true }
      ]
    },
    children: [
      {
        path: 'courses',
        name: 'route-superadmin',
        prefix: 'admin',
        page: 'page-admin-courses'
      },
      {
        path: 'groups/:subjectId',
        name: 'route-superadmin-groups',
        prefix: 'multiple',
        page: 'page-groups'
      },
      {
        // The admin users page: for a superadmin it covers every course of the
        // subject (the server widens the scope on the superadmin router).
        path: 'students/:subjectId',
        name: 'route-superadmin-students',
        prefix: 'admin',
        page: 'page-admin-students'
      },
      {
        path: 'calendar/:subjectId',
        name: 'route-superadmin-calendar',
        prefix: 'multiple',
        page: 'page-calendar'
      },
      {
        path: 'settings/:subjectId',
        name: 'route-superadmin-subject-settings',
        prefix: 'superadmin',
        page: 'page-superadmin-subject-settings'
      },
      {
        path: 'media',
        name: 'route-superadmin-media',
        prefix: 'multiple',
        page: 'page-media'
      }
    ]
  },
  {
    path: 'auth',
    name: 'route-signin',
    prefix: 'user',
    page: 'page-signin',
    meta: { hasNavbar: false },
    beforeEnter: (to, from, next) => {
      if (localStorage.getItem('accessToken')) {
        return next({
          name: 'route-student',
          params: { locale: to.params.locale }
        });
      }
      return next();
    }
  },
  {
    // Where the email's code panel links: the address prefilled, the code
    // typed. A page of its own because requesting a new link — the only
    // other way to reach a code field — revokes the grant this code is from.
    path: 'auth/code',
    name: 'route-signin-code',
    prefix: 'user',
    page: 'page-signin',
    meta: { hasNavbar: false },
    props: { initialStage: 'code' },
    beforeEnter: (to, from, next) => {
      if (localStorage.getItem('accessToken')) {
        return next({
          name: 'route-student',
          params: { locale: to.params.locale }
        });
      }
      return next();
    }
  },
  {
    // Where a magic link lands, token in the fragment. The page spends it with
    // a POST — the server's GET only redirects, so a mail scanner fetching the
    // link spends nothing. No signed-in guard: a link from an email is an
    // explicit request to become that account, even over an existing session.
    path: 'auth/verify',
    name: 'route-signin-verify',
    prefix: 'user',
    page: 'page-signin',
    meta: { hasNavbar: false },
    props: { initialStage: 'verify' }
  },
  {
    path: 'join/:subjectId/:groupId',
    name: 'route-group-join',
    prefix: 'user',
    page: 'page-group-join',
    meta: { hasNavbar: false },
    props: (to) => ({
      subjectId: to.params.subjectId,
      groupId: to.params.groupId
    })
  },
  {
    path: 'confirm',
    name: 'confirm',
    prefix: 'user',
    page: 'page-email-confirm',
    meta: { hasNavbar: false }
  },
  {
    path: 'privacy',
    name: 'route-privacy',
    prefix: 'multiple',
    page: 'page-privacy',
    meta: { keepAlive: true }
  },
  {
    path: 'certificate/:role?/:subjectId/:courseId',
    name: 'route-certificate',
    prefix: 'student',
    page: 'page-student-certificate',
    beforeEnter: async (to, from) => {
      await store.dispatch('getCertificate', { userId: to.query.userId, courseId: to.params.courseId });
    }
  },
  {
    path: 'certificate-check/:role?',
    name: 'route-certificate-check',
    prefix: 'multiple',
    page: 'page-certificate-check'
  },
  {
    path: 'certificate-printable/:subjectId?/:courseId?',
    name: 'route-certificate-printable',
    prefix: 'multiple',
    page: 'page-certificate-printable',
    meta: { hasNavbar: false },
    props: (to) => {
      return {
        userId: to.query.userId ?? store.state.userdata._id,
        subjectId: to.params.subjectId || undefined,
        courseId: to.params.courseId || undefined,
        lang: to.query.lang || 'uk'
      };
    }
  },
  {
    path: 'course/:role(student)?/:subjectId/:courseId',
    prefix: 'student',
    page: 'page-student-course',
    meta: { requiresAuth: true },
    props: (to) => ({
      tabs: [
        { icon: 'lessons', label: 'router.tab.lessons', route: 'route-student-course-lessons' },
        { icon: 'favorites', label: 'router.tab.favorites', route: 'route-student-course-favorites' },
        { icon: 'stats', label: 'router.tab.statistics', route: 'route-student-course-stats' },
        { icon: 'notes', label: 'router.tab.notes', route: 'route-student-course-notes' },
        { icon: 'materials', label: 'router.tab.materials', route: 'route-student-course-materials' },
        {
          icon: 'sandbox',
          label: 'router.tab.sandbox',
          route: 'route-student-course-sandbox',
          courseOption: 'hasSandbox'
        },
        { icon: 'rating', label: 'router.tab.rating', route: 'route-student-course-rating', courseOption: 'hasRating' }
      ]
    }),
    children: [
      {
        path: '',
        name: 'route-student-course-lessons',
        prefix: 'student',
        page: 'page-student-course-lessons',
        beforeEnter: async (to, from) => {
          await store.dispatch('getLessons', { courseId: to.params.courseId });
        },
        props: (to) => ({
          courseId: to.params.courseId,
          module: to.query.module,
          lessonId: to.query.lessonId
        })
      },
      {
        path: 'favorites',
        name: 'route-student-course-favorites',
        prefix: 'student',
        page: 'page-student-course-favorites',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'stats',
        name: 'route-student-course-stats',
        prefix: 'student',
        page: 'page-student-course-stats',
        props: (to) => ({
          courseId: to.params.courseId,
          userId: store.state.userdata._id
        })
      },
      {
        path: 'notes',
        name: 'route-student-course-notes',
        prefix: 'student',
        page: 'page-student-course-notes',
        props: (to) => ({
          courseId: to.params.courseId,
          itemId: to.query.itemId
        })
      },
      {
        path: 'materials',
        name: 'route-student-course-materials',
        prefix: 'student',
        page: 'page-student-course-materials',
        props: (to) => ({
          courseId: to.params.courseId,
          itemId: to.query.itemId,
          view: to.query.view
        })
      },
      {
        path: 'sandbox',
        name: 'route-student-course-sandbox',
        prefix: 'student',
        page: 'page-student-course-sandbox',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'rating',
        name: 'route-student-course-rating',
        prefix: 'student',
        page: 'page-student-course-rating',
        props: (to) => ({ courseId: to.params.courseId })
      }
    ]
  },
  {
    path: 'course/:role(admin|superadmin)/:subjectId/:courseId',
    prefix: 'admin',
    page: 'page-admin-course',
    meta: { requiresAuth: true },
    props: (to) => ({
      tabs: [
        { icon: 'lessons', label: 'router.tab.content', route: 'route-admin-course-lessons' },
        { icon: 'settings', label: 'router.tab.settings', route: 'route-admin-course-settings' },
        { icon: 'glossary', label: 'router.tab.dictionary', route: 'route-admin-course-glossary' },
        { icon: 'tips', label: 'router.tab.cheat_sheets', route: 'route-admin-course-tips', courseOption: 'hasTips' },
        { icon: 'notifications_active', label: 'router.tab.notifications', route: 'route-admin-course-notifications' },
        {
          icon: 'bolt',
          label: 'router.tab.access_automation',
          route: 'route-admin-course-access-automation',
          businessOnly: true
        }
      ]
    }),
    children: [
      {
        path: '',
        name: 'route-admin-course-lessons',
        prefix: 'admin',
        page: 'page-admin-course-lessons',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'settings',
        name: 'route-admin-course-settings',
        prefix: 'admin',
        page: 'page-admin-course-settings',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'glossary',
        name: 'route-admin-course-glossary',
        prefix: 'admin',
        page: 'page-admin-course-glossary',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'tips',
        name: 'route-admin-course-tips',
        prefix: 'admin',
        page: 'page-admin-course-tips',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'notifications',
        name: 'route-admin-course-notifications',
        prefix: 'admin',
        page: 'page-admin-course-notifications',
        props: (to) => ({ courseId: to.params.courseId })
      },
      {
        path: 'access-automation',
        name: 'route-admin-course-access-automation',
        prefix: 'admin',
        page: 'page-admin-course-access-automation',
        props: (to) => ({ courseId: to.params.courseId })
      }
    ]
  },
  {
    path: 'course/:role(teacher)/:subjectId/:courseId',
    prefix: 'teacher',
    page: 'page-teacher-course',
    meta: { requiresAuth: true },
    props: (to) => ({
      tabs: [{ icon: 'content', label: 'router.tab.content', route: 'route-teacher-course-lessons' }]
    }),
    children: [
      {
        path: '',
        name: 'route-teacher-course-lessons',
        prefix: 'teacher',
        page: 'page-teacher-course-lessons',
        beforeEnter: async (to, from) => {
          await store.dispatch('getLessons', { courseId: to.params.courseId });
        }
      }
    ]
  },
  {
    path: 'course-stats/:role/:subjectId/:courseId',
    name: 'route-course-stats',
    prefix: 'multiple',
    page: 'page-course-stats',
    meta: { requiresAuth: true },
    props: (to) => ({
      tabs: [
        { icon: 'stats-students', label: 'router.tab.student_analytics', route: 'route-course-stats-students' },
        { icon: 'stats-lessons', label: 'router.tab.lesson_analytics', route: 'route-course-stats-lessons' },
        { icon: 'arrow-down', label: 'router.tab.infographics', route: 'route-course-stats-funnels' }
      ]
    }),
    children: [
      {
        path: 'students',
        name: 'route-course-stats-students',
        prefix: 'multiple',
        page: 'page-course-stats-students'
      },
      {
        path: 'lessons',
        name: 'route-course-stats-lessons',
        prefix: 'multiple',
        page: 'page-course-stats-lessons'
      },
      {
        path: 'funnels',
        name: 'route-course-stats-funnels',
        prefix: 'multiple',
        page: 'page-course-stats-funnels',
        props: (to) => ({ courseId: to.params.courseId })
      }
    ]
  },
  {
    path: 'survey/:subjectId/:courseId/:type(intro|outro)',
    name: 'route-survey',
    prefix: 'student',
    page: 'page-student-course-survey',
    meta: { requiresAuth: true, keepAlive: true },
    props: (to, from) => ({
      type: to.params.type,
      subjectId: to.params.subjectId,
      courseId: to.params.courseId
    })
  },
  {
    path: 'lesson/:role(student)?/:subjectId/:lessonId',
    name: 'route-lesson',
    prefix: 'student',
    page: 'page-student-lesson',
    meta: { requiresAuth: true, keepAlive: true },
    props: (to, from) => ({
      lessonId: to.params.lessonId,
      taskIndex: Number(to.query.taskIndex)
    }),
    beforeEnter: async (to, from) => {
      await store.dispatch('getTasks', { lessonId: to.params.lessonId });
    }
  },
  {
    path: 'lesson/:role(admin|superadmin)/:subjectId/:lessonId',
    name: 'route-lesson-editable',
    prefix: 'admin',
    page: 'page-admin-lesson',
    meta: { requiresAuth: true },
    props: (to, from) => ({
      lessonId: to.params.lessonId,
      taskIndex: Number(to.query.taskIndex)
    }),
    beforeEnter: async (to, from) => {
      await store.dispatch('getTasks', { lessonId: to.params.lessonId });
    }
  },
  {
    path: 'lesson-stats/:role(teacher)/:subjectId/:lessonId',
    name: 'route-lesson-stats',
    prefix: 'multiple',
    page: 'page-lesson-stats',
    meta: { requiresAuth: true },
    props: (to, from) => ({
      lessonId: to.params.lessonId
    })
  },
  {
    path: 'lesson-stats/:role(admin|superadmin)/:subjectId/:lessonId',
    name: 'route-admin-lesson-stats',
    prefix: 'admin',
    page: 'page-admin-lesson-stats',
    meta: { requiresAuth: true },
    props: (to, from) => ({
      lessonId: to.params.lessonId
    })
  },
  {
    path: 'user/:role?/:subjectId/:courseId',
    name: 'route-user',
    prefix: 'multiple',
    page: 'page-user',
    meta: { requiresAuth: true },
    props: (to, from) => ({
      courseId: to.params.courseId,
      userId: to.query.userId
    })
  },
  {
    path: 'tip-printable/:subjectId/:tipId',
    name: 'route-tip-printable',
    prefix: 'multiple',
    page: 'page-tip-printable',
    meta: { hasNavbar: false },
    props: (to, from) => ({
      tipId: to.params.tipId
    })
  },
  {
    path: 'settings/:role(student|teacher)',
    name: 'route-settings',
    prefix: 'multiple',
    page: 'page-settings',
    meta: { requiresAuth: true }
  },
  {
    path: 'onboarding/:role(student|teacher|admin)',
    name: 'route-onboarding',
    prefix: 'user',
    page: 'page-user-setup',
    meta: { requiresAuth: true }
  },
  {
    path: ':pathMatch(.*)*',
    prefix: 'user',
    page: 'page-404'
  }
];

const routeParse = function (route) {
  if (route.page) route.component = async (_) => await import(`./pages/${route.prefix}/${route.page}.vue`);
  if (route.dispatch) route.beforeEnter = async (_) => await store.dispatch(route.dispatch, {});
  if (route.children) route.children = route.children.map((e) => routeParse(e));
  return route;
};

routes = routes.map((e) => routeParse(e));

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
  routes: [
    {
      path: '/:locale(uk|en)',
      component: (_) => import('@/layouts/layout-empty.vue'),
      children: routes
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: (to) => {
        const supported = ['en', 'uk'];
        const browserLang = navigator.language.slice(0, 2);
        const lang = supported.includes(browserLang) ? browserLang : 'uk';
        // A location, not a string: `/${lang}${to.path}` alone dropped the
        // query and the hash of every locale-less URL. Every server redirect
        // arrives without a locale — `/callback/login?originalUrl=…` lost the
        // course it was meant to land on, `/auth?reason=…` lost its message,
        // and `/auth/verify#token=…` lost the token itself.
        return { path: `/${lang}${to.path}`, query: to.query, hash: to.hash };
      }
    }
  ]
});

router.onError((error, to) => {
  //if (import.meta.env.VITE_ENV != 'development') return
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    console.error('Router error:', error);
    const t = new Date().getTime();
    let newLocation = to.fullPath;
    if (!to?.fullPath) newLocation = window.location.pathname;
    if (newLocation.includes('?')) newLocation += `&reload=${t}`;
    else newLocation += `?reload=${t}`;
    window.location.href = newLocation;
  }
});

let interval;

router.beforeEach(async (to, from, next) => {
  interval = setInterval((_) => {
    store.state.loadingProgress += 0.5 ** store.state.loadingStep++;
  }, 200);

  const isAuthorized = localStorage.getItem('accessToken');

  const locale = to.params.locale;
  const localePrev = store.state.locale;
  store.commit('updateState', { locale });
  if (localePrev !== locale) {
    try {
      await tolgee.changeLanguage(locale);
    } catch (err) {
      // Don't let a failed i18n fetch stall navigation — Tolgee falls back to
      // bundled staticData for the target language.
      console.error(`tolgee.changeLanguage(${locale}) failed, using bundled translations`, err);
      window?.faro?.api?.pushError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  // LOCAL DEV: Always show signin page
  // if (to.name !== 'route-privacy') {
  //   return next({
  //     name: 'route-privacy',
  //     params: { locale: to.params.locale },
  //   })
  // }

  if (store.state.isPageUnloadPrevented) {
    const res = window.confirm('router.message.unsaved_changes');
    if (res) next();
    else return next(false);
  }

  if (to.meta?.requiresAuth && !isAuthorized) {
    return next({
      name: 'route-signin',
      params: { locale: tolgee.getLanguage() }
    });
  }

  window.faro?.api?.setView({
    name: window.location.href
  });

  let role = to.params.role ?? to.path.split('/')[2];
  role = ['student', 'teacher', 'admin', 'superadmin'].includes(role) ? role : 'student';
  const subjectId = to.params?.subjectId;

  const rolePrev = store.state.role;
  const isRoleChanged = role != rolePrev;
  if (isRoleChanged) {
    store.commit('setRole', role);
    if (isAuthorized) {
      await Promise.all([store.dispatch('getUserdata'), store.dispatch('getSubject'), store.dispatch('getSubjects')]);
      posthog.identify(store.state.userdata._id, { email: store.state.userdata.email, role });
      window?.faro?.api?.setUser({
        _id: store.state.userdata._id,
        email: store.state.userdata.email,
        attributes: { role }
      });
    }
  }

  const subjectIdPrev = store.state.subjectId;

  if (subjectId && subjectId != subjectIdPrev) {
    store.commit('setSubjectId', subjectId);
    await store.dispatch('getSubject');
  }

  // The bell is deliberately NOT awaited: navigation must not wait on it. Only
  // the unread count is fetched here, and the list is loaded when the drawer is
  // actually opened. A failure is swallowed for the same reason — a bell that
  // cannot load is not a reason to block a page.
  if (isAuthorized) {
    Promise.resolve(
      // getSubjects only runs on role change, so on a cold load of an unchanged
      // role subjectsList can still be empty — and the count uses it to tell the
      // server which subjects to read.
      store.state.subjectsList?.length ? undefined : store.dispatch('getSubjects')
    )
      .then((_) => store.dispatch('getNotificationsCount'))
      .catch((_) => {});
  }

  const isOnboardingOverrideActive =
    !!store.state.userdata?.isAdminDev && store.state.adminDevSettings?.onboardingCompleteOverride === false;

  // Employees found in the directory never see the survey: the server fills
  // their profile from it (`employeeProfile`).
  const isOnboardingIncomplete =
    (!store.state.userdata?.rolesInitialized?.[role] && !store.state.userdata?.employeeProfile) ||
    isOnboardingOverrideActive;
  const isOnboardingComplete = !isOnboardingIncomplete;

  if (
    isAuthorized &&
    isOnboardingIncomplete &&
    !['route-onboarding', 'route-main'].includes(to.name) &&
    !['admin', 'superadmin'].includes(role)
  ) {
    return next({
      name: 'route-onboarding',
      params: { role, locale: to.params.locale }
    });
  }

  if (isAuthorized && isOnboardingComplete && to.name === 'route-onboarding') {
    return next({
      name: `route-${role}`,
      params: { locale: to.params.locale }
    });
  }

  next();
});

router.afterEach((_) => {
  // After the navigation has committed, so a report can never delay or
  // interfere with a redirect. Swallows its own failures.
  recordNotificationArrival(!!localStorage.getItem('accessToken'));

  store.state.loadingStep = 1;
  store.state.loadingProgress = null;
  clearInterval(interval);
  document.body.style.overflowY = 'auto';
  store.commit('preventUnload', false);
  store.commit('showLoader', false);
});

export default router;
